import { Camera01, RefreshCw01 } from "@untitledui/icons"
import type { CameraStatus } from "./use-camera"

// What the camera screen says when it can't show the camera. Never a dead
// end: the phone's own camera always works.

function permissionSteps(userAgent: string): string {
  if (/iPhone|iPad|iPod/i.test(userAgent)) {
    return "In Safari, tap aA in the address bar, then Website Settings, and set Camera to Allow."
  }
  if (/Android/i.test(userAgent)) {
    return "In Chrome, tap the icon left of the web address, then Permissions, and turn Camera on."
  }
  return "Allow the camera for this site in your browser settings."
}

export function CameraProblem({
  status,
  onRetry,
  onUseNative,
}: {
  status: Extract<CameraStatus, "denied" | "unavailable" | "interrupted">
  onRetry: () => void
  onUseNative: () => void
}) {
  if (status === "interrupted") {
    return (
      <div className="absolute inset-0 z-30 grid place-items-center bg-black/80 px-8">
        <button
          type="button"
          onClick={onRetry}
          className="flex h-14 items-center gap-2 rounded-2xl bg-white px-6 text-[16px] font-semibold text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
        >
          <RefreshCw01 className="size-5" aria-hidden />
          Tap to resume the camera
        </button>
      </div>
    )
  }
  const denied = status === "denied"
  return (
    <div role="alert" className="absolute inset-0 z-30 flex flex-col justify-center bg-zinc-950 px-7 text-white">
      <h2 className="text-[24px] font-bold leading-tight text-white">
        {denied ? "The camera is blocked" : "Couldn't open the camera"}
      </h2>
      <p className="mt-3 text-[16px] leading-relaxed text-white/75">
        {denied
          ? `No problem. You can use your phone's own camera instead. Or turn the camera on for this page. ${permissionSteps(navigator.userAgent)}`
          : "Another app may be using it. You can use your phone's own camera instead, or try again."}
      </p>
      <div className="mt-8 space-y-3">
        <button
          type="button"
          onClick={onUseNative}
          className="flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-white text-[16px] font-semibold text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
        >
          <Camera01 className="size-5" aria-hidden />
          Use my phone's camera
        </button>
        <button
          type="button"
          onClick={onRetry}
          className="flex h-12 w-full items-center justify-center rounded-2xl text-[15px] font-semibold text-white/80 hover:text-white focus-visible:outline-2 focus-visible:outline-white"
        >
          Try again
        </button>
      </div>
    </div>
  )
}
